import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidgetData } from './useWidgetData';
import KPIWidget from './widgets/KPIWidget';
import BarChartWidget from './widgets/BarChartWidget';
import DonutWidget from './widgets/DonutWidget';
import LineChartWidget from './widgets/LineChartWidget';
import GaugeWidget from './widgets/GaugeWidget';
import AIInsightsWidget from './widgets/AIInsightsWidget';
import TextCardWidget from './widgets/TextCardWidget';
import AlertListWidget from './widgets/AlertListWidget';
import SeasonalWidget from './widgets/SeasonalWidget';
import TableWidget from './widgets/TableWidget';
import DynamicComparisonWidget from './widgets/DynamicComparisonWidget';
import DynamicComparisonConfigModal from './widgets/DynamicComparisonConfigModal';
import TrafficWidget from './widgets/TrafficWidget';

// Map chart_type from catalog -> React component
const RENDERERS = {
    kpi: KPIWidget,
    sparkline: KPIWidget,
    bar: BarChartWidget,
    column: BarChartWidget,
    line: LineChartWidget,
    area: LineChartWidget,
    donut: DonutWidget,
    pie: DonutWidget,
    polar: DonutWidget,
    radial_gauge: GaugeWidget,
    text_card: TextCardWidget,
    alert_list: AlertListWidget,
    seasonal_compare: SeasonalWidget,
    table: TableWidget,
};

// Specific widget_id -> renderer override (takes precedence over chart_type).
// Lets the catalog ship a `seasonal_performance` widget without needing a
// new chart_type string.
const RENDERERS_BY_ID = {
    seasonal_performance: SeasonalWidget,
    ai_insights: AIInsightsWidget,
    dynamic_comparison: DynamicComparisonWidget,
    traffic_overview: TrafficWidget,
};

// Widgets that draw their own controls and handle their own loading / error /
// empty states. The card renders them unconditionally and hands over the fetch
// state, instead of swapping the body for a skeleton — a widget whose property
// picker vanishes on every refetch can't be used to change the property.
const SELF_CONTROLLED = new Set(['traffic_overview']);

// Widgets that require a per-instance config modal before they can fetch data.
// Map widget_id -> modal component. Add new entries here when other widgets
// need configurable inputs (e.g. multi-series compare, custom KPI formula).
const CONFIG_MODALS = {
    dynamic_comparison: DynamicComparisonConfigModal,
};

/**
 * Widget card — shell + renderer dispatch.
 * Handles loading, error, and dispatches by widget chart_type.
 */
// widget_id -> display label override.
// Used when the catalog seed label is stale because the widget is no longer
// scoped to a fixed period (the dashboard now has a date filter chip).
const LABEL_OVERRIDES = {
    total_sales_mtd: 'Total Sales',
};

export default function WidgetCard({ widget, filters, paused, onRemove, onExpand, config, onConfigChange }) {
    const ConfigModal = CONFIG_MODALS[widget.widget_id];
    const hasConfig = !!(config && Object.keys(config).length);
    const needsConfig = !!ConfigModal && !hasConfig;
    const selfControlled = SELF_CONTROLLED.has(widget.widget_id);
    const [showConfig, setShowConfig] = useState(false);

    // Merge per-instance config into the filters sent to the data endpoint.
    // useWidgetData's filterKey includes a stable hash of `config` so any change
    // here triggers a refetch.
    const effectiveFilters = useMemo(() => {
        // Self-controlled widgets always send config, even empty: their own
        // selection is the whole point, and an absent config key would silently
        // fall back to the dashboard filter bar they deliberately ignore.
        if (!hasConfig && !selfControlled) return filters;
        return { ...filters, config: config || {} };
    }, [filters, config, hasConfig, selfControlled]);

    // Pause the data fetch when this widget still needs a config — otherwise the
    // backend would 400 on every render and the user would see an error blob
    // instead of the friendly "configure me" CTA.
    const effectivePaused = paused || needsConfig;

    const { data, loading, error, refetch } = useWidgetData(widget.widget_id, effectiveFilters, { paused: effectivePaused });

    const Renderer = RENDERERS_BY_ID[widget.widget_id]
        || RENDERERS[data?.chart_type || widget.chart_type]
        || KPIWidget;
    const { t } = useTranslation();
    const isAi = widget.requires_ai || widget.category === 'ai_driven' || widget.category === 'command_center';
    const displayLabel = LABEL_OVERRIDES[widget.widget_id] || widget.label;

    const openConfig = () => setShowConfig(true);
    const closeConfig = () => setShowConfig(false);
    const handleSaveConfig = (nextConfig) => {
        if (onConfigChange) onConfigChange(nextConfig);
        setShowConfig(false);
    };

    return (
        <div className={`v3-card ${isAi ? 'v3-ai-glow' : ''}`} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '12px 18px 0', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span className="drag-handle" style={{ cursor: 'grab', fontSize: 14, color: 'var(--v3-text-dim)', userSelect: 'none' }}>⋮⋮</span>
                {isAi && <span className="v3-ai-tag">AI</span>}
                <span className="v3-label" style={{ flex: 1 }}>{displayLabel}</span>
                {ConfigModal && (
                    <button
                        type="button"
                        onClick={openConfig}
                        title={t('Configure widget')}
                        aria-label={`${t('Configure widget')}: ${displayLabel}`}
                        style={{
                            cursor: 'pointer', color: 'var(--v3-text-dim)', fontSize: 14,
                            padding: '0 4px', userSelect: 'none', background: 'transparent',
                            border: 'none', lineHeight: 1,
                        }}
                    >⚙</button>
                )}
                <button
                    type="button"
                    onClick={onRemove}
                    title={t('Remove widget')}
                    aria-label={`${t('Remove widget')}: ${displayLabel}`}
                    style={{
                        cursor: 'pointer', color: 'var(--v3-text-dim)', fontSize: 16,
                        padding: '0 4px', userSelect: 'none', background: 'transparent',
                        border: 'none', lineHeight: 1,
                    }}
                >×</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflow: 'hidden', marginTop: 4 }}>
                {selfControlled && (
                    <Renderer
                        data={data}
                        loading={loading}
                        error={error}
                        refetch={refetch}
                        widgetId={widget.widget_id}
                        widget={widget}
                        config={config}
                        onConfigChange={onConfigChange}
                    />
                )}
                {!selfControlled && needsConfig && (
                    <div style={{ padding: 20 }}>
                        <div className="v3-body" style={{ marginBottom: 10 }}>
                            {t('This widget needs configuration. Pick two entities to compare.')}
                        </div>
                        <button onClick={openConfig} className="v3-btn v3-btn-primary">{t('Configure')}</button>
                    </div>
                )}
                {!selfControlled && !needsConfig && loading && (
                    <div className="v3-skeleton-wrap" aria-busy="true" aria-label={t('Loading')}>
                        <div className="v3-skeleton v3-skeleton-num" />
                        <div className="v3-skeleton v3-skeleton-line" style={{ width: '62%' }} />
                        <div className="v3-skeleton v3-skeleton-line" style={{ width: '88%' }} />
                        <div className="v3-skeleton v3-skeleton-line" style={{ width: '74%' }} />
                    </div>
                )}
                {!selfControlled && !needsConfig && error && (
                    <div style={{ padding: 20 }}>
                        <div className="v3-body" style={{ color: 'var(--v3-red)' }}>{t('Error')}: {error}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button onClick={refetch} className="v3-btn v3-btn-ghost">{t('Retry')}</button>
                            {ConfigModal && (
                                <button onClick={openConfig} className="v3-btn v3-btn-ghost">{t('Reconfigure')}</button>
                            )}
                        </div>
                    </div>
                )}
                {!selfControlled && !needsConfig && !loading && !error && data && <Renderer data={data} widgetId={widget.widget_id} widget={widget} />}
            </div>

            {ConfigModal && showConfig && (
                <ConfigModal
                    initial={config || {}}
                    onSave={handleSaveConfig}
                    onClose={closeConfig}
                />
            )}
        </div>
    );
}
